import React from "react";
import { PageHero, CTA } from "@/components/PageHero";
import { TrendingUp, Users, Clock, ArrowRight } from "lucide-react";

const CASES = [
  {
    client: "MedCare Hospital",
    industry: "Healthcare",
    problem: "Paper-based patient records causing 2+ hour delays in patient care.",
    solution: "Deployed cloud-based Hospital Management System with OPD, pharmacy and billing.",
    outcomes: [{ icon: Clock, v: "60% faster", l: "patient check-in" }, { icon: Users, v: "10k+", l: "patients managed" }, { icon: TrendingUp, v: "35%", l: "revenue increase" }],
  },
  {
    client: "SmartLearn Academy",
    industry: "Education",
    problem: "Manual admissions and fee tracking across 5 campuses.",
    solution: "Custom School ERP with mobile app for parents and staff.",
    outcomes: [{ icon: Clock, v: "80%", l: "less admin work" }, { icon: Users, v: "3,500", l: "students onboarded" }, { icon: TrendingUp, v: "2x", l: "fee collection speed" }],
  },
  {
    client: "TrackFleet Logistics",
    industry: "Logistics",
    problem: "No visibility into fleet movement or delivery status.",
    solution: "Real-time GPS tracking with driver app and customer notifications.",
    outcomes: [{ icon: Clock, v: "25%", l: "faster deliveries" }, { icon: Users, v: "120", l: "vehicles tracked" }, { icon: TrendingUp, v: "40%", l: "customer satisfaction ↑" }],
  },
];

import { useSEO } from "@/hooks/useSEO";

export default function CaseStudies() {
  useSEO({
    title: "Case Studies — Easyworks Industries",
    description: "Real stories, real results. See how Easyworks Industries has helped businesses grow through technology, automation and digital transformation.",
  });
  return (
    <div data-testid="case-studies-page">
      <PageHero title="Case Studies" subtitle="Real stories. Real results. See how we've helped businesses grow." />
      <section className="py-20 space-y-8">
        <div className="container mx-auto max-w-7xl px-6 space-y-8">
          {CASES.map((c, i) => (
            <div key={i} className="p-8 rounded-2xl bg-white border border-slate-200 grid lg:grid-cols-3 gap-8" data-testid={`case-${i}`}>
              <div className="lg:col-span-2">
                <div className="text-[11px] tracking-[0.2em] font-semibold text-[#2563EB] uppercase mb-2">{c.industry}</div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>{c.client}</h3>
                <div className="mb-4">
                  <div className="text-[13px] font-semibold text-slate-900 mb-1">Challenge</div>
                  <p className="text-[14px] text-slate-600 leading-relaxed">{c.problem}</p>
                </div>
                <div>
                  <div className="text-[13px] font-semibold text-slate-900 mb-1">Solution</div>
                  <p className="text-[14px] text-slate-600 leading-relaxed">{c.solution}</p>
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 p-5 space-y-4">
                <div className="text-[11px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Outcomes</div>
                {c.outcomes.map((o, j) => {
                  const Icon = o.icon;
                  return (
                    <div key={j} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-md bg-white border border-slate-200 flex items-center justify-center"><Icon className="w-4 h-4 text-[#2563EB]" /></div>
                      <div>
                        <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{o.v}</div>
                        <div className="text-[12px] text-slate-500">{o.l}</div>
                      </div>
                    </div>
                  );
                })}
                <a href="/contact" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#2563EB]">Discuss your project <ArrowRight className="w-3.5 h-3.5" /></a>
              </div>
            </div>
          ))}
        </div>
      </section>
      <CTA />
    </div>
  );
}
